import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';

import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';

import { alpha, styled } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DocumentToJiraWizard from './user-story-agent/DocumentToJiraWizard';
import GenerateTestCasesAgent from './GenerateTestCasesAgent';
import { useUserAccess } from '../../hooks/useRole';
import GenerateTestPlanModal from '../../components/project-dashboard/test-plans/GenerateTestPlanModal';

const AGENTS = [
  {
    id: 'user-story-agent',
    title: 'User Story Agent',
    description:
      'Generate Jira stories, acceptance criteria, and test cases from documents or Confluence pages.',
    icon: AutoStoriesRoundedIcon,
    badge: 'New',
  },
  {
    id: 'generate-test-cases',
    title: 'Generate Test Cases',
    description:
      'Create AI-powered test cases from a scenario, Jira story, uploaded document, or pasted requirements.',
    icon: AutoAwesomeRoundedIcon,
    badge: 'New',
  },
  {
    id: 'defect-intelligence',
    title: 'Defect Intelligence',
    description:
      'Classify, deduplicate, and surface insights from your Jira defects using AI-powered analysis.',
    icon: BugReportRoundedIcon,
  },
  {
    id: 'test-intelligence',
    title: 'Test Intelligence',
    description:
      'Analyze your test cases and requirements to generate prioritized test plan suggestions for your next cycle.',
    icon: AssistantRoundedIcon,
  },
];

const ROUTE_AGENTS = {
  'defect-intelligence': () => '/defect-intelligence',
};

const AgentGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(3),
}));

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
  width: 260,
  borderRadius: 16,
  border: `1px solid ${disabled
    ? theme.palette.divider
    : alpha(theme.palette.primary.main, 0.2)
    }`,
  transition: 'all 0.2s ease',
  backgroundColor: theme.palette.background.paper,
  opacity: disabled ? 0.65 : 1,
  overflow: 'hidden',

  '&:hover': disabled
    ? {}
    : {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[4],
      borderColor: alpha(theme.palette.primary.main, 0.35),
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
}));

const AgentCard = ({ agent, onSelect }) => {
  const { id, title, description, icon: Icon, badge, comingSoon, disabledReason } = agent;
  const isDisabled = comingSoon || !!disabledReason;

  return (
    <StyledCard disabled={isDisabled}>
      <CardActionArea
        disabled={isDisabled}
        onClick={() => onSelect(id)}
        onKeyDown={(e) => {
          if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onSelect(id);
          }
        }}
        sx={{ height: '100%', p: 3 }}
      >
        <Stack spacing={2} alignItems="center" textAlign="center" sx={{ height: '100%' }}>
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', minHeight: 24 }}>
            {comingSoon ? (
              <Chip label="Coming Soon" size="small" />
            ) : disabledReason ? (
              <Chip label={disabledReason} size="small" color="warning" />
            ) : (
              badge && <Chip label={badge} size="small" color="primary" />
            )}
          </Box>

          <IconWrapper>
            <Icon sx={{ fontSize: 34 }} />
          </IconWrapper>

          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {description}
          </Typography>
        </Stack>
      </CardActionArea>
    </StyledCard>
  );
};


const AgenticAiPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [activeAgent, setActiveAgent] = useState(null);
  const { isFullAccess, projectIds } = useUserAccess();
  const canAccessDefectIntelligence =
    isFullAccess || projectIds.includes(projectId);

  // Modal-based agents: id → component
  const AGENT_COMPONENTS = useMemo(
    () => ({
      'user-story-agent': DocumentToJiraWizard,
      'generate-test-cases': GenerateTestCasesAgent,
      'test-intelligence': GenerateTestPlanModal,
    }),
    []
  );

  const agents = useMemo(
    () =>
      AGENTS.map((agent) =>
        agent.id === 'defect-intelligence' && !canAccessDefectIntelligence
          ? { ...agent, disabledReason: 'Admin only' }
          : agent,
      ),
    [canAccessDefectIntelligence],
  );

  const handleSelect = (id) => {
    if (ROUTE_AGENTS[id]) {
      navigate(ROUTE_AGENTS[id](projectId));
    } else {
      setActiveAgent(id);
    }
  };

  const ActiveComponent = AGENT_COMPONENTS[activeAgent];

  const handleClose = () => setActiveAgent(null);

  return (
    <Container maxWidth={false} sx={{ mt: 3, pb: 4 }}>
      <Stack spacing={4}>
        {/* Header */}
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
            AI Agents
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Intelligent agents that automate your engineering workflow.
          </Typography>
        </Box>

        {/* Agent Cards */}
        <AgentGrid>
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onSelect={handleSelect}
            />
          ))}
        </AgentGrid>
      </Stack>

      {/* Modal Agent Wizard */}
      {ActiveComponent && (
        <ActiveComponent
          open
          onClose={handleClose}
          handleClose={handleClose}
          projectId={projectId}
        />
      )}
    </Container>
  );
};

export default AgenticAiPage;